<template>
<div class="chapter">
<div class="inner_page">
<h2 class="h2x"><a id="page_xxvi"></a><a id="page_xxvii"></a><a id="lan"></a>NOTE ON LANGUAGE AND TRANSLITERATION</h2>
<p class="noindent">Several important matters should be clarified for the reader. The first point concerns the original Azerbaijani names and titles that I have transliterated and translated into English. Some of them appear in Russian sources in a distorted way, having been rendered according to Russian pronunciation rules. This, subsequently, has created discrepancies in transliteration in scholarly sources, with different spellings appearing within the English-language musicological literature. For example, the name of the composer Jovdat Hajiyev (Cövdət Hacıyev) appears in various sources as Jevdet Hajiyev, Jevdet Gadjiyev, or Dzhevdet Gadzhiev. Besides, several names are spelled various ways, although for reasons unrelated to the Russian language. For example, the composer Frangiz Alizade in some English-language publications appears as Franghiz Ali-Zadeh (Alizadeh), and in German sources as Frangis Ali-Sade. The last name of the jazz musician Vagif Mustafazade is also shown as Mustafa-Zadeh in some publications. The spelling of the name of the traditional singer Alim Qasimov does not follow official transliteration rules, either (it should be Alim Gasimov). In my book, the orthography of all names is consistent throughout. I have chosen a spelling based on transliteration from the Azerbaijani language. If I am translating from a primary source that uses a different spelling, then my chosen spelling appears in brackets or parentheses.</p>
<p class="indent">Another concern refers to some last names that have been modified after Azerbaijan’s independence in late 1991. The Russian suffix “-ov” sometimes has been replaced with “-li” to reveal the nation’s Turkic origins. The name of the composer Uzeyir Hajibeyli – who was known during the Soviet era as Uzeyir Hajibeyov – is the most representative example. The reversion to the pre-Soviet spelling of his name was officially announced in Azerbaijan in 2008, and since then, respective changes have been made in all domains of Azerbaijani society and culture that contain references to Hajibeyli. Accordingly, although this composer is known in all English-language musicological sources as Hajibeyov (Hajibekov, Gadzhibekov, Gadjibekov), I think it is essential to refer to the pioneer of the composed music in the country by recognizing his original Azerbaijani name, Hajibeyli.</p>
<p class="indent"><a id="page_xxviii"></a>The third matter that needs clarification is “Azerbaijani,” one of the most important terms in the present research. Several alternative versions of the English translation of this adjective exist, including “Azeri” and “Azerbaijanian” (“Azerbaidzhanian”). In the entire range of publications related to Azerbaijan, all three versions are used interchangeably, although each has its own historical, ethnographical, and lexicological justification. In my original text, I use Azerbaijani; however, the other two versions also appear throughout the book within quoted texts.</p>
<p class="indent">The fourth issue relates to pronunciation rules for texts in the Azerbaijani language that are used within quotations in musical examples of vocal pieces. Over its history, the Azerbaijani language has experienced three major alphabet changes. Arabic script was used for nearly a thousand years until Latin became the official script in 1929. In 1939, Cyrillic replaced Latin and remained in use until 1991, when a modified Latin was adopted as the official Azerbaijani alphabet. I have provided the transliteration table for the current Azerbaijani alphabet; this table uses the symbols of the International Phonetic Alphabet:</p>
<p class="tabcaption"><strong>Table 0.1. Pronunciation of Azerbaijani alphabet</strong></p>
<table border="0" cellpadding="0" cellspacing="0" width="100%">
<colgroup><col width="10%"/>
<col width="90%"/>
</colgroup><tbody><tr>
<td valign="top"><p class="table">A a</p></td>
<td valign="top"><p class="table1">[<span class="fontx">ɑ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">B b</p></td>
<td valign="top"><p class="table1">[b]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">C c</p></td>
<td valign="top"><p class="table1">[d<span class="fontx">ʒ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">Ç ç</p></td>
<td valign="top"><p class="table1">[t<span class="fontx">ʃ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">D d</p></td>
<td valign="top"><p class="table1">[d]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">E e</p></td>
<td valign="top"><p class="table1">[e]</p></td>
</tr>
<tr>
<td valign="top"><p class="table"><span class="fontx">Ə ə</span></p></td>
<td valign="top"><p class="table1">[æ]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">F f</p></td>
<td valign="top"><p class="table1">[f]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">G g</p></td>
<td valign="top"><p class="table1">[<span class="fontx">ɡʲ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">Ğ ğ</p></td>
<td valign="top"><p class="table1">[<span class="fontx">ɣ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">H h</p></td>
<td valign="top"><p class="table1">[h]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">X x</p></td>
<td valign="top"><p class="table1">[x]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">I I</p></td>
<td valign="top"><p class="table1">[<span class="fontx">ɯ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">İ i</p></td>
<td valign="top"><p class="table1">[<span class="fontx">ɪ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">J j</p></td>
<td valign="top"><p class="table1">[<span class="fontx">ʒ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">K k</p></td>
<td valign="top"><p class="table1">[k]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">Q q</p></td>
<td valign="top"><p class="table1">[<span class="fontx">ɡ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">L l</p></td>
<td valign="top"><p class="table1">[l]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">M m</p></td>
<td valign="top"><p class="table1">[m]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">N n</p></td>
<td valign="top"><p class="table1">[n]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">O o</p></td>
<td valign="top"><p class="table1">[<span class="fontx">ɔ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">Ö ö</p></td>
<td valign="top"><p class="table1">[œ]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">P p</p></td>
<td valign="top"><p class="table1">[p]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">R r</p></td>
<td valign="top"><p class="table1">[r]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">S s</p></td>
<td valign="top"><p class="table1">[s]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">Ş ş</p></td>
<td valign="top"><p class="table1">[<span class="fontx">ʃ</span>]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">T t</p></td>
<td valign="top"><p class="table1">[t]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">U u</p></td>
<td valign="top"><p class="table1">[u]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">Ü ü</p></td>
<td valign="top"><p class="table1">[y]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">V v</p></td>
<td valign="top"><p class="table1">[v]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">Y y</p></td>
<td valign="top"><p class="table1">[j]</p></td>
</tr>
<tr>
<td valign="top"><p class="table">Z z</p></td>
<td valign="top"><p class="table1">[z]</p></td>
</tr>
</tbody></table>
<p class="indentt">For the transliteration of any portions of my text that are taken from Russian-language sources, I have used the Library of Congress system, although in the modified form. I have followed all the rules of this system, except for the ligatures and diacritical marks.</p>
<p class="indent">One final remark refers to the way of differentiating musicians who have the same last name but different first names: Agshin Alizade and Frangiz Alizade; Fikrat Amirov and Jamil Amirov; Afrasiyab Badalbeyli and Farhad <a id="page_xxix"></a>Badalbeyli; Gara Garayev and Faraj Garayev; Fidan Gasimova and Khuraman Gasimova; Tofig Guliyev and Javanshir Guliyev; Soltan Hajibeyov and Ismayil Hajibeyov; Rauf Hajiyev and Jovdat Hajiyev. To avoid any confusion throughout the text, I have added, as needed, the initial of the first name: A. Alizade or F. Alizade, G. Garayev or F. Garayev, etc. This is necessary in the sections that involve the discussion of both musicians having identical last names; I skip the initial if the section focuses on one of the musicians only.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// translation.vue?394fe5cc